/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import useQualificationUpload from '../Qualification/helper/useQualificationUpload';

export const MyQualificationPopup = ({ open, row, onCancelChanges, onApplyChanges, onChange }) => {
  const [draft, setDraft] = useState(row || {});
  const [saving, setSaving] = useState(false);
  const { selectFile, cancelConversion, isConverting, fileError } = useQualificationUpload();

  useEffect(() => setDraft(row || {}), [row]);

  const setFields = (changes) => {
    setDraft((current) => ({ ...current, ...changes }));
    Object.entries(changes).forEach(([name, value]) => onChange({ target: { name, value } }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    selectFile(file, (prepared) => setFields({ qualificationUrl: prepared, _attachmentFromQbo: false }));
  };

  const handleSave = async () => {
    if (saving || isConverting || fileError) return;
    setSaving(true);
    try {
      await onApplyChanges();
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line no-undef
  const isFile = (value) => typeof File !== 'undefined' && value instanceof File;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (saving) return;
        cancelConversion();
        onCancelChanges();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Qualification details</DialogTitle>
      <DialogContent style={{ display: 'grid', gap: 16, paddingTop: 12 }}>
        <TextField label="Qualification" value={draft.name || ''} disabled fullWidth />
        <TextField
          label="Reference number"
          value={draft.referenceNumber || ''}
          onChange={(event) => setFields({ referenceNumber: event.target.value })}
          fullWidth
        />
        <TextField
          label="Issue date"
          type="date"
          value={draft.issueDate || ''}
          onChange={(event) => setFields({ issueDate: event.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label="Expiry date"
          type="date"
          value={draft.expiryDate || ''}
          onChange={(event) => setFields({ expiryDate: event.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <div>
          <Button component="label" variant="outlined" disabled={isConverting || saving}>
            {isConverting ? 'Converting…' : draft.qualificationUrl ? 'Replace file' : 'Upload image or PDF'}
            <input hidden type="file" accept="image/*,.heic,.heif,.pdf" onChange={handleFileChange} />
          </Button>
          {draft.qualificationUrl && (
            <span style={{ marginLeft: 10 }}>
              {isFile(draft.qualificationUrl) ? draft.qualificationUrl.name : 'Current attachment'}
            </span>
          )}
          {draft.qualificationUrl && (
            <Button onClick={() => setFields({ qualificationUrl: null, _attachmentFromQbo: false })}>Remove</Button>
          )}
          {fileError && (
            <div role="alert" style={{ color: '#c62828' }}>
              {fileError}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (saving) return;
            cancelConversion();
            onCancelChanges();
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || isConverting || Boolean(fileError)}
        >
          {saving ? <CircularProgress size={18} /> : 'Submit for review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MyQualificationPopup.propTypes = {
  open: PropTypes.bool,
  row: PropTypes.object,
  onCancelChanges: PropTypes.func,
  onApplyChanges: PropTypes.func,
  onChange: PropTypes.func
};

export const PopupEditing = React.memo(({ popupComponent: Popup, setEmployeesQualifications, saveRow }) => (
  <Plugin>
    <Template name="popupEditing">
      <TemplateConnector>
        {(
          { rows, getRowId, addedRows, editingRowIds, createRowChange, rowChanges },
          { changeRow, stopEditRows, cancelChangedRows }
        ) => {
          // Added qualifications are edited in the grid draft row. This popup edits existing rows only.
          if (!editingRowIds.length || addedRows.length) return null;

          const [rowId] = editingRowIds;
          const sourceRow = rows.find((item) => getRowId(item) === rowId);
          if (!sourceRow) return null;
          const editedRow = { ...sourceRow, ...(rowChanges[rowId] || {}) };
          const rowIds = editingRowIds;

          const onChange = ({ target: { name, value } }) => {
            changeRow({ rowId, change: createRowChange(editedRow, value, name) });
          };

          const close = () => {
            stopEditRows({ rowIds });
            cancelChangedRows({ rowIds });
          };

          const applyChanges = async () => {
            const saved = await saveRow(editedRow);
            if (!saved) return;
            const status = saved.status || editedRow.status || 'Pending';
            const isPending = String(status).toLowerCase() === 'pending';
            setEmployeesQualifications((current) =>
              current.map((item) =>
                getRowId(item) === rowId ? { ...item, ...editedRow, ...saved, status, _pending: isPending } : item
              )
            );
            close();
          };

          return (
            <Popup
              open={editingRowIds.length > 0}
              row={editedRow}
              onChange={onChange}
              onApplyChanges={applyChanges}
              onCancelChanges={close}
              rows={rows}
              setEmployeesQualifications={setEmployeesQualifications}
            />
          );
        }}
      </TemplateConnector>
    </Template>
    <Template name="root">
      <TemplatePlaceholder />
      <TemplatePlaceholder name="popupEditing" />
    </Template>
  </Plugin>
));

PopupEditing.displayName = 'MyQualificationPopupEditing';
PopupEditing.propTypes = {
  popupComponent: PropTypes.elementType.isRequired,
  setEmployeesQualifications: PropTypes.func.isRequired,
  rows: PropTypes.array,
  saveRow: PropTypes.func.isRequired
};
